import { Typography } from '@euler/app/components/typography';
import { ColorStop } from '@euler/app/tv/theme/types';
import { Center } from '@euler/components/Center';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { usePatchedAnimatedTextInputRef } from '@euler/functions/usePatchedAnimatedTextInputRef.web';
import { usePatchedSvgPathRef } from '@euler/functions/usePatchedSvgPathRef';
import { deg2rad } from '@euler/utils/svg';
import { pathOfArc } from '@euler/utils/svg/arcUtil';
import { nanoid } from 'nanoid/non-secure';
import { memo, useEffect, useMemo } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { TextInput } from 'react-native-gesture-handler';
import Animated, {
  interpolateColor,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Defs, LinearGradient, Path, Stop } from 'react-native-svg';

const AnimatedText = Animated.createAnimatedComponent(TextInput);
const AnimatedPath = Animated.createAnimatedComponent(Path);

export type ScoreViewProps = {
  outerArcColor: string;
  innerArcBgColor: string;
  innerArcColorStops: ColorStop[];
  width: number;
  height: number;
  score: number;
  label?: string;
  subLabel?: string;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  labelStyle?: StyleProp<TextStyle>;
  subLabelStyle?: StyleProp<TextStyle>;
  textContainerStyle?: StyleProp<ViewStyle>;
  colorStops: ColorStop[];
};

const kAngleDeltaInDegree = 30;
const kStartAngleInDegree = 270 - kAngleDeltaInDegree;
const kEndAngleInDegree = 450 + kAngleDeltaInDegree;
const kOuterStrokeWidth = 3;
const kInnerStrokeWidth = 40;

const kInnerRadiusRatio = 285 / 358;

type Metrics = {
  r1: number;
  r2: number;
  cx: number;
  cy: number;
  height: number;
};

export const ScoreView = memo(
  ({
    score,
    label = '综合得分',
    subLabel = 'SYNTHESIZED SCORE',
    outerArcColor,
    innerArcBgColor,
    innerArcColorStops,
    width,
    height,
    style,
    textContainerStyle,
    textStyle,
    labelStyle,
    subLabelStyle,
    colorStops,
  }: ScoreViewProps) => {
    const { rpx } = useResponsiveContext();
    const outerStrokeWidth = rpx(kOuterStrokeWidth);
    const innerStrokeWidth = rpx(kInnerStrokeWidth);
    const { cx, cy, r1, r2 } = useScoreViewMetrics({
      width,
      outerStrokeWidth,
    });
    const outerCirclePath = pathOfArc(
      cx,
      cy,
      r1,
      kStartAngleInDegree,
      kEndAngleInDegree,
      true,
      true,
    );
    const innerCirclePath = pathOfArc(
      cx,
      cy,
      r2,
      kStartAngleInDegree,
      kEndAngleInDegree,
      true,
      true,
    );
    const scorePath = pathOfArc(
      cx,
      cy,
      r2,
      kStartAngleInDegree,
      kEndAngleInDegree,
      true,
      true,
    );

    const gradientFillId = useMemo(() => nanoid(), []);

    const scorePathRef = usePatchedSvgPathRef();
    const scoreTextRef = usePatchedAnimatedTextInputRef();

    const progress = useSharedValue(0);
    const c = makeInnerArcDashArray(r2);

    const animatedProps = useAnimatedProps(() => {
      const p = progress.value;
      // return property as 'strokeDashoffset' does not work on web,
      // have to use 'stroke-dashoffset' instead, but this will trigger another
      // error: Invalid DOM property `stroke-dashoffset`. Did you mean `strokeDashoffset`
      // we actively ignore this error for now.
      return Platform.OS === 'web'
        ? ({
            'stroke-dashoffset': (1 - p) * c,
          } as any)
        : {
            strokeDashoffset: (1 - p) * c,
          };
    });

    const scoreProps = useAnimatedProps(() => {
      return { text: Math.floor(100 * progress.value).toString() } as any;
    });

    const scoreStyle = useAnimatedStyle(() => {
      const currentScore = score * progress.value;
      const p = currentScore / 100;
      return {
        color: interpolateColor(
          p,
          colorStops.map(x => x[0]),
          colorStops.map(x => x[1]),
        ),
      };
    });

    useEffect(() => {
      progress.value = withTiming(score / 100, { duration: 1000 });
    }, [progress, score]);

    if (width <= 0 || height <= 0) return null;

    return (
      <View style={style}>
        <Svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          fill="none"
        >
          <Path
            d={outerCirclePath}
            strokeWidth={outerStrokeWidth}
            stroke={outerArcColor}
          />
          <Path
            d={innerCirclePath}
            strokeWidth={innerStrokeWidth}
            stroke={innerArcBgColor}
          />
          <AnimatedPath
            ref={scorePathRef}
            d={scorePath}
            strokeWidth={innerStrokeWidth}
            stroke={`url(#${gradientFillId})`}
            strokeDasharray={c}
            animatedProps={animatedProps}
          />
          <Defs>
            <LinearGradient id={gradientFillId}>
              {innerArcColorStops.map((colorStop, i) => (
                <Stop key={i} stopColor={colorStop[1]} offset={colorStop[0]} />
              ))}
            </LinearGradient>
          </Defs>
        </Svg>
        <Center
          css={`
            padding-top: ${r1 - r2}px;
          `}
          style={[StyleSheet.absoluteFill, textContainerStyle]}
        >
          <AnimatedText
            editable={false}
            showSoftInputOnFocus={false}
            pointerEvents={'none'}
            textAlign="center"
            textAlignVertical="center"
            ref={scoreTextRef}
            css={`
              text-align: center;
              padding: 0;
              align-self: stretch;
              font-family: ${FontFamily.NotoSans.Bold};
            `}
            style={[textStyle, scoreStyle]}
            animatedProps={scoreProps}
          />
          <Typography.Label
            regular
            css={`
              text-align: center;
            `}
            style={labelStyle}
          >
            {label}
          </Typography.Label>
          {subLabel ? (
            <Typography.Label
              regular
              css={`
                text-align: center;
              `}
              style={subLabelStyle}
            >
              {subLabel}
            </Typography.Label>
          ) : null}
        </Center>
      </View>
    );
  },
);

export function useScoreViewMetrics(args: {
  width: number;
  outerStrokeWidth?: number;
}): Metrics {
  const { rpx } = useResponsiveContext();
  const { width } = args;
  const outerStrokeWidth = args.outerStrokeWidth ?? rpx(kOuterStrokeWidth);
  const r1 = (width - outerStrokeWidth) / 2; // outer radius
  const r2 = kInnerRadiusRatio * r1; // inner radius
  const [cx, cy] = [r1, r1];
  const height =
    r1 +
    outerStrokeWidth +
    r1 * Math.sin((kAngleDeltaInDegree / 180) * Math.PI);
  return { r1, r2, cx, cy, height };
}

function makeInnerArcDashArray(r: number) {
  return r * deg2rad(kEndAngleInDegree - kStartAngleInDegree);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function angleOfScore(score: number) {
  const total = 180 + 2 * kAngleDeltaInDegree;
  return kStartAngleInDegree + (score / 100) * total;
}
